import { useState } from 'react'
import { Trophy, Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useConfiguracionesFidelizacion,
  useDeleteConfiguracionFidelizacion,
} from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { FidelizacionFormDialog } from '../components/FidelizacionFormDialog'
import { TestConnectionButton } from '../components/TestConnectionButton'
import { EstadoConexionBadge } from '../components/EstadoConexionBadge'
import type { ConfiguracionFidelizacionListItem } from '../types'

export function ConfiguracionFidelizacionPage() {
  const { data, isLoading, error } = useConfiguracionesFidelizacion()
  const deleteMut = useDeleteConfiguracionFidelizacion()

  const [sheetOpen, setSheetOpen] = useState(false)
  const [editing, setEditing] = useState<ConfiguracionFidelizacionListItem | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<ConfiguracionFidelizacionListItem | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handleNew = () => {
    setEditing(null)
    setSheetOpen(true)
  }
  const handleEdit = (c: ConfiguracionFidelizacionListItem) => {
    setEditing(c)
    setSheetOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    setDeleteError(null)
    try {
      await deleteMut.mutateAsync(confirmDelete.id)
      setConfirmDelete(null)
    } catch (err) {
      setDeleteError(parseFidelizacionError(err).message)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="h-6 w-6" />
            Configuración Fidelización
          </h1>
          {data && (
            <Badge className="badge-gradient text-xs">
              {data.length} configuración{data.length === 1 ? '' : 'es'}
            </Badge>
          )}
        </div>
        <Button onClick={handleNew} className="bg-blue-800 hover:bg-blue-900 text-white">
          <Plus className="mr-2 h-4 w-4" />
          Nueva configuración
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(error).message}</AlertDescription>
        </Alert>
      )}

      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Grupo</TableHead>
                <TableHead>API base</TableHead>
                <TableHead>API key</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data && data.length > 0 ? (
                data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="font-medium">{c.nombre}</div>
                      <div className="text-xs text-muted-foreground">{c.slug}</div>
                    </TableCell>
                    <TableCell>{c.grupo_nombre}</TableCell>
                    <TableCell className="max-w-xs truncate text-xs text-muted-foreground" title={c.api_base_url}>
                      {c.api_base_url}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.api_key_masked}</TableCell>
                    <TableCell>
                      <EstadoConexionBadge
                        ultimoUso={c.ultimo_uso}
                        ultimoError={c.ultimo_error}
                        activo={c.activo}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2">
                        <TestConnectionButton configId={c.id} />
                        <Button size="sm" variant="outline" onClick={() => handleEdit(c)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDelete(c)}
                          className="text-pink-600 hover:text-pink-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-sm text-muted-foreground">
                    No hay configuraciones todavía. Crea la primera con el botón "Nueva configuración".
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <FidelizacionFormDialog
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        configuracion={editing}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar configuración</DialogTitle>
            <DialogDescription>
              Esta acción es definitiva. Se borrarán también los logs de auditoría asociados.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          {confirmDelete && (
            <p className="text-sm">
              ¿Eliminar <strong>{confirmDelete.nombre}</strong> ({confirmDelete.grupo_nombre})?
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} disabled={deleteMut.isPending}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={deleteMut.isPending}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              {deleteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
